/**
 * 教材领取 · **单张报名单的领取页**（`/logistics/:registrationId`）
 * -----------------------------------------------------------------------------
 * 2026-10-22 改版（用户拍板）：教材按**报名单**领 —— 列表（`/logistics/track`）
 * 里点哪一单就进哪一单，一单一份收件信息，互不覆盖。原来这一页固定取「最近一张
 * 报名单」，等于整个 App 只有一份领取信息。
 *
 * 本页三态：
 *   ① 只读（默认）：收件人 / 手机号 / 收件地址三行 + 卡头「修改」；
 *   ② 编辑（点了「修改」才进）：改完点「确定修改」才发请求，点「取消」丢弃改动；
 *   ③ 锁定（`materialShippedAt` 非空，即已出库）：不能改，只能看 —— 服务端
 *      `LOGISTICS-006` 会硬拦（发货了再改会寄丢），前端别让人白填一遍。
 *      ⚠️ 「已提交领取（待发货）」**不在**锁定范围内：那一步只是意向，地址仍可改。
 *
 * 按钮随教材状态走（2026-10-22，四态口径）：
 *   待申请领取 → 「领取」（确认收件信息 + 提交领取申请，落 `PENDING_SHIP`）
 *   待发货 / 已发货 → 不给按钮，只给说明（等仓库 / 等签收）
 *   已领取 → 已完成提示；异常 → 引导联系客服
 *
 * 口径（不改）：
 *   - 一期**不接入快递商系统**，运单号不进学员端，只显示发放状态；
 *   - 教材**运费到付**，系统不结算运费；
 *   - 发货满 5 个自然日由服务端自动确认「已领取」（一期无轨迹回调的兜底）。
 */
import {
  CN_MOBILE_PATTERN,
  DeliveryMethod,
  formatRegionText,
  isRegionComplete,
  isRegionDetailFilled,
  MATERIAL_STATUS_LABEL,
  MaterialStatus,
} from '@enroll/shared';
import { Toast } from 'antd-mobile';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { describeApiError } from '../api/errors';
import { claimMaterial, saveAddress } from '../api/h5';
import { useRegistration } from '../api/hooks';
import type { ShippingAddressInput } from '../api/types';
import { RegionField } from '../components/FormRenderer/RegionField';
import {
  Button,
  Card,
  ErrorBlock,
  KV,
  LoadingBlock,
  Notice,
  PageShell,
} from '../components/layout';

const EMPTY_ADDRESS: ShippingAddressInput = {
  receiverName: '',
  phone: '',
  provinceCode: '',
  cityCode: '',
  districtCode: '',
  detail: '',
};

// 缴费确认后主状态才转 ENROLLED —— 教材发放从这里开始
const PAID_STATUS = 'ENROLLED';

export default function LogisticsTrack() {
  const navigate = useNavigate();
  const { registrationId } = useParams<{ registrationId: string }>();
  const detail = useRegistration(registrationId);

  const [address, setAddress] = useState<ShippingAddressInput>(EMPTY_ADDRESS);
  const [hydrated, setHydrated] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const loaded = detail.data;
  /**
   * 回填只做一次。
   *
   * 保存成功后 `detail` 会重新拉取，如果不设这道闸，用户正在编辑到一半的内容
   * 会被服务端那份覆盖掉（表现是「打着字突然回退」）。
   */
  useEffect(() => {
    if (!loaded || hydrated) return;
    setHydrated(true);
    setAddress(loaded.address ?? EMPTY_ADDRESS);
  }, [loaded, hydrated]);

  const materialStatus =
    (loaded?.materialStatus as MaterialStatus | undefined) ?? MaterialStatus.PENDING_APPLY;
  const isMail = (loaded?.deliveryMethod ?? DeliveryMethod.MAIL) === DeliveryMethod.MAIL;
  /**
   * 发货后不允许自助改地址（`LOGISTICS-006`）。
   * 判据是 `materialShippedAt` 而不是状态码：`ABNORMAL` 也是发过货之后才出现的，
   * 用「已出库」这一事实判断，语义上不会漏。
   */
  const editable = isMail && !loaded?.materialShippedAt;
  const paid = loaded?.status === PAID_STATUS;
  const received = materialStatus === MaterialStatus.RECEIVED;
  /** 只有「待申请领取」才给领取按钮；其余状态按钮会误导成「还能再点一次」 */
  const canClaim = isMail && materialStatus === MaterialStatus.PENDING_APPLY;

  /** 提交前校验：错误就地 Toast 第一条，别让学员猜哪里没填 */
  const validate = useCallback((value: ShippingAddressInput): string | null => {
    if (!value.receiverName.trim()) return '请填写收件人姓名';
    if (!CN_MOBILE_PATTERN.test(value.phone.trim())) return '收件人手机号格式不正确';
    if (!isRegionComplete(value)) return '请选择完整的省 / 市 / 区';
    if (!isRegionDetailFilled(value)) return '请填写详细地址';
    return null;
  }, []);

  const persist = useCallback(
    async (value: ShippingAddressInput): Promise<boolean> => {
      if (!registrationId) return false;
      setBusy(true);
      try {
        await saveAddress(registrationId, {
          ...value,
          receiverName: value.receiverName.trim(),
          phone: value.phone.trim(),
        });
        await detail.refetch();
        return true;
      } catch (error) {
        // 已发货 / 现场领取这两种 409 的文案服务端已经写清楚了，直接透出比自造一句准
        Toast.show({
          icon: 'fail',
          content: error instanceof ApiError ? describeApiError(error) : '保存失败，请稍后重试',
        });
        return false;
      } finally {
        setBusy(false);
      }
    },
    [registrationId, detail],
  );

  /** 「确定修改」：校验 → 落库 → 退回只读态 */
  const onConfirmEdit = useCallback(async () => {
    const message = validate(address);
    if (message) {
      Toast.show({ icon: 'fail', content: message });
      return;
    }
    if (!(await persist(address))) return;
    setEditing(false);
    Toast.show({ icon: 'success', content: '收件信息已修改' });
  }, [address, validate, persist]);

  /**
   * 底部「领取」：**先落地址、再提交领取申请**（两步，顺序不能反）。
   *
   * 服务端的 `material-claim` 会校验地址完整性，没先存地址会吃 `LOGISTICS-002`。
   * 地址不完整时直接把学员推进编辑态 —— 比只弹一句 Toast 更好用。
   */
  const onClaim = useCallback(async () => {
    if (!registrationId) return;
    const message = validate(address);
    if (message) {
      setEditing(true);
      Toast.show({ icon: 'fail', content: message });
      return;
    }
    if (!(await persist(address))) return;
    setBusy(true);
    try {
      await claimMaterial(registrationId);
      await detail.refetch();
      setEditing(false);
      Toast.show({ icon: 'success', content: '已提交领取，待仓库发货' });
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: error instanceof ApiError ? describeApiError(error) : '提交领取失败，请稍后重试',
      });
    } finally {
      setBusy(false);
    }
  }, [registrationId, address, validate, persist, detail]);

  if (!registrationId) {
    return (
      <PageShell title="教材领取" back solidNav>
        <div className="state-block">
          <div className="state-block__title">没有指定报名单</div>
          <div className="state-block__text">教材按报名单领取，请从列表里选一张</div>
          <Button
            variant="ghost"
            size="sm"
            className="state-block__action"
            onClick={() => navigate('/logistics/track')}
          >
            去教材领取列表
          </Button>
        </div>
      </PageShell>
    );
  }

  if (detail.isPending) {
    return (
      <PageShell title="教材领取" back solidNav>
        <LoadingBlock text="正在加载领取信息" />
      </PageShell>
    );
  }

  if (detail.error || !loaded) {
    return (
      <PageShell title="教材领取" back solidNav>
        <ErrorBlock
          message={detail.error ? describeApiError(detail.error) : '请稍后重试'}
          onRetry={() => void detail.refetch()}
        />
      </PageShell>
    );
  }

  const shown = loaded.address ?? null;
  const hasAddress = Boolean(shown && isRegionComplete(shown) && isRegionDetailFilled(shown));

  return (
    <PageShell
      title="教材领取"
      back
      solidNav
      footer={
        /* 只有「待申请领取」的邮寄单才需要点这一下；其余状态给的是说明，不是按钮 */
        canClaim && !editing ? (
          <Button variant="primary" block loading={busy} onClick={() => void onClaim()}>
            领取
          </Button>
        ) : undefined
      }
    >
      {paid ? null : (
        <Notice tone="warning">缴费确认后才会安排教材发放，请先在「缴费」中完成转账。</Notice>
      )}

      <Card title="发放信息" tight>
        <KV label="报名单号" value={<span className="mono-num">{loaded.registrationNo}</span>} />
        <KV label="报名类型" value={`${loaded.enrollTypeName} · ${loaded.batchName}`} />
        <KV label="发放状态" value={MATERIAL_STATUS_LABEL[materialStatus]} />
        <KV label="领取方式" value={isMail ? '邮寄（运费到付）' : '现场领取'} />
      </Card>

      {isMail ? (
        <Card
          title="收件信息"
          action={
            editable && !editing ? (
              <Button variant="ghost" size="mini" onClick={() => setEditing(true)}>
                修改
              </Button>
            ) : undefined
          }
        >
          {editing ? (
            <>
              <label className="field">
                <span className="field__label">
                  收件人<em className="field__required">*</em>
                </span>
                <span className="field__control">
                  <input
                    className="field-input field-input--left"
                    value={address.receiverName}
                    placeholder="请输入收件人姓名"
                    onChange={(event) =>
                      setAddress((prev) => ({ ...prev, receiverName: event.target.value }))
                    }
                  />
                </span>
              </label>
              <label className="field">
                <span className="field__label">
                  手机号<em className="field__required">*</em>
                </span>
                <span className="field__control">
                  <input
                    className="field-input field-input--left"
                    inputMode="numeric"
                    maxLength={11}
                    value={address.phone}
                    placeholder="请输入收件人手机号"
                    onChange={(event) =>
                      setAddress((prev) => ({ ...prev, phone: event.target.value }))
                    }
                  />
                </span>
              </label>
              <div className="field">
                <span className="field__label">
                  所在地区<em className="field__required">*</em>
                </span>
                <span className="field__control">
                  <RegionField
                    value={address}
                    onChange={(next) => setAddress((prev) => ({ ...prev, ...next }))}
                    onBlur={() => undefined}
                  />
                </span>
              </div>
              <div className="btn-row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    // 取消要**丢弃**改动：重新用服务端那份盖回来，否则下次进来还是改了一半的值
                    setAddress(shown ?? EMPTY_ADDRESS);
                    setEditing(false);
                  }}
                >
                  取消
                </Button>
                <Button variant="primary" loading={busy} onClick={() => void onConfirmEdit()}>
                  确定修改
                </Button>
              </div>
            </>
          ) : (
            <>
              <KV label="收件人" value={shown?.receiverName || '未填写'} />
              {/* 手机号不脱敏：收件信息是寄件凭据，学员必须能核对自己填的号 */}
              <KV label="手机号" value={shown?.phone || '未填写'} />
              <KV label="收件地址" value={(shown && formatRegionText(shown)) || '未填写'} />
              <p className="field__help">
                {editable
                  ? hasAddress
                    ? '收件人与手机号已按报名信息自动填好，如需调整点右上角「修改」'
                    : '地址还没填完，点「修改」补齐后才能提交领取'
                  : '教材已发出，如需修改收件信息请联系客服'}
              </p>
            </>
          )}
        </Card>
      ) : (
        <Notice tone="info">本期教材为现场领取，无需填写收件地址，按通知到指定地点领取。</Notice>
      )}

      {materialStatus === MaterialStatus.PENDING_SHIP ? (
        <Notice tone="info">已提交领取，正在等待仓库发货，发货后会通过公众号通知你。</Notice>
      ) : null}
      {materialStatus === MaterialStatus.SHIPPED ? (
        <Notice tone="info">教材已发出，签收后请留意。发货满 5 天系统会自动确认「已领取」。</Notice>
      ) : null}
      {materialStatus === MaterialStatus.ABNORMAL ? (
        <Notice tone="warning">发放异常，请通过下方「联系客服」反馈，附上报名单号。</Notice>
      ) : null}
      {received ? <Notice tone="success">教材已领取完成，本单无需再操作。</Notice> : null}

      <Card title="说明" tight>
        <ul className="bullet-list">
          <li className="bullet-list__item">
            <span className="bullet-list__dot" />
            <span>教材寄出后会通过公众号消息通知你</span>
          </li>
          <li className="bullet-list__item">
            <span className="bullet-list__dot" />
            <span>系统不结算运费，签收时由快递公司到付</span>
          </li>
          <li className="bullet-list__item">
            <span className="bullet-list__dot" />
            <span>一个报名单领一套教材，多单请分别领取</span>
          </li>
          <li className="bullet-list__item">
            <span className="bullet-list__dot" />
            <span>发放异常请通过「联系客服」反馈，附上报名单号</span>
          </li>
        </ul>
      </Card>

      <Button variant="ghost" block onClick={() => navigate('/help/service')}>
        联系客服
      </Button>
    </PageShell>
  );
}
